<template>
    <b-container id="download-center-page" fluid>
        <b-row>
            <b-col cols="12">
                <div class="border-bottom mb-3 pb-3">
                    <h3>다운로드 센터</h3>
                    <p class="fw-400 text-secondary mb-0">
                        요청된 다운로드 파일을 관리하고 다운로드합니다.
                    </p>
                </div>
            </b-col>
        </b-row>

        <b-row class="mb-3">
            <b-col cols="12">
                <b-card no-body bg-variant="white">
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <h5 class="mb-0">다운로드 검색하기</h5>

                            <div v-if="listLoading">
                                <div class="d-flex align-items-center" style="height: 36px">
                                    <b-spinner class="mr-2" variant="primary" style="height: 1rem; width: 1rem;"/>
                                    <p class="d-inline-block mb-0">조회중...</p>
                                </div>
                            </div>
                            <div v-else class="d-flex align-items-center">
                                <b-button form="download-center-search-form"
                                          type="reset"
                                          class="mr-2"
                                          :disabled="listLoading"
                                >
                                    {{ $t('초기화') }}
                                </b-button>
                                <b-button
                                    variant="primary"
                                    :disabled="listLoading"
                                    form="download-center-search-form"
                                    type="submit"
                                >
                                    {{ $t('검색하기') }}
                                </b-button>
                            </div>
                        </div>
                    </template>

            <div class="p-3">
                <b-form id="download-center-search-form"
                        autocomplete="new-password"
                        @submit.stop.prevent="searchData"
                        @reset.prevent="resetSearch"
                >
                    <b-row>
                        <b-col cols="12" md="3" class="mb-3 mb-xl-0">
                            <b-form-group
                                id="fieldset-input-status"
                                label="상태"
                                label-for="input-status"
                                class="mb-0"
                            >
                                <b-form-select id="input-status"
                                               v-model="searchFormValue.status"
                                               :disabled="listLoading"
                                >
                                    <b-form-select-option v-for="data in statusOptions"
                                                          :key="`status-${data.value}`"
                                                          :value="data.value">{{ data.text }}
                                    </b-form-select-option>
                                </b-form-select>
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="3" class="mb-3 mb-xl-0">
                            <b-form-group
                                id="fieldset-input-filename"
                                label="파일명"
                                label-for="input-filename"
                                class="mb-0"
                            >
                                <b-form-input id="input-filename"
                                              v-model="searchFormValue.filename"
                                              :disabled="listLoading"
                                              placeholder="파일명 검색"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="3" class="mb-3 mb-xl-0">
                            <b-form-group
                                id="fieldset-input-from-date"
                                label="시작일"
                                label-for="input-from-date"
                                class="mb-0"
                            >
                                <b-form-datepicker id="input-from-date"
                                                   v-model="searchFormValue.fromDate"
                                                   :disabled="listLoading"
                                                   placeholder="시작일 선택"
                                                   locale="ko"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="3">
                            <b-form-group
                                id="fieldset-input-to-date"
                                label="종료일"
                                label-for="input-to-date"
                                class="mb-0"
                            >
                                <b-form-datepicker id="input-to-date"
                                                   v-model="searchFormValue.toDate"
                                                   :disabled="listLoading"
                                                   placeholder="종료일 선택"
                                                   locale="ko"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row class="mt-3">
                        <b-col cols="12">
                            <b-form-checkbox v-model="searchFormValue.includeOthers" :disabled="listLoading">
                                다른 사용자의 다운로드 포함
                            </b-form-checkbox>
                        </b-col>
                    </b-row>
                </b-form>
            </div>
        </b-card>
            </b-col>
        </b-row>

        <!-- Selection Toolbar -->
        <b-card v-if="selectedFiles.length > 0" 
                no-body 
                class="mt-3 selection-toolbar"
                bg-variant="light"
        >
            <div class="p-3 d-flex justify-content-between align-items-center flex-wrap">
                <div class="selection-info d-flex align-items-center">
                    <strong class="mr-2">{{ selectedFiles.length }}개 파일 선택됨</strong>
                    <span>({{ formatFileSize(selectedTotalSize) }})</span>
                    <b-badge v-if="selectedTotalSize > sizeWarningThreshold" 
                             variant="warning" 
                             class="ml-2"
                    >
                        대용량 파일 경고
                    </b-badge>
                </div>
                <div class="selection-actions d-flex flex-wrap" style="gap: 8px;">
                    <b-button size="sm" variant="dark" :disabled="downloadProgress.active" @click="showSelectedFilesModal">
                        <fa icon="eye" class="mr-1"></fa>선택 파일 보기
                    </b-button>
                    <b-button size="sm" variant="dark-border" :disabled="downloadProgress.active" @click="clearSelection">
                        <fa icon="times-circle" class="mr-1"></fa>선택 해제
                    </b-button>
                    <b-button size="sm" :disabled="downloadProgress.active" @click="downloadSelectedFiles(false)">
                        <fa icon="download" class="mr-1"></fa>개별 다운로드
                    </b-button>
                    <b-button size="sm" :disabled="downloadProgress.active" @click="downloadSelectedFiles(true)">
                        <fa icon="download" class="mr-1"></fa>ZIP 다운로드
                    </b-button>
                </div>
            </div>

            <!-- Progress Bar -->
            <div v-if="downloadProgress.active" class="px-3 pb-3 d-flex align-items-center">
                <b-progress :max="downloadProgress.total" height="24px" show-progress animated class="flex-grow-1">
                    <b-progress-bar :value="downloadProgress.current" :label="downloadProgress.text"/>
                </b-progress>
                <b-button v-if="zipAbort" size="sm" variant="outline-secondary" class="ml-2" @click="cancelZip">
                    취소
                </b-button>
            </div>
        </b-card>

        <!-- Downloads Table -->
        <b-row class="mt-3">
            <b-col cols="12">
                <b-card
                    :class="[{ 'shadow-sm': searchResults || (!listLoading && searchResults) }, 'list-container']"
                    :bg-variant="!searchResults ? 'light' : 'white'"
                    no-body
                >
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <h6 class="mb-0">다운로드 목록</h6>
                            </div>
                            <div class="d-flex align-items-center">
                                <b-button variant="outline-secondary" 
                                          size="sm"
                                          :disabled="listLoading"
                                          @click="refreshList"
                                >
                                    <i class="bi bi-arrow-clockwise mr-1"></i>새로고침
                                </b-button>
                            </div>
                        </div>
                    </template>
                    <!-- 목록이 이미 있을 때 다시 불러오기에 실패하면 기존 목록을 두고 알린다 (첫 조회 실패는 빈 상태 자리에 표시) -->
                    <b-alert v-if="loadError && searchResults && searchResults.length" show variant="danger"
                             class="m-3 py-2 d-flex align-items-center justify-content-between"
                    >
                        <span>목록을 새로 불러오지 못했습니다. {{ loadError }}</span>
                        <b-button size="sm" variant="outline-danger" :disabled="listLoading" @click="refreshList">다시 시도</b-button>
                    </b-alert>
                    <div class="table-container">
                        <b-table
                            fixed responsive
                            :items="searchResults"
                            :fields="tableColumns"
                            class="mb-0"
                            show-empty
                            :busy="listLoading"
                        >
                            <template #empty>
                                <PageEmptyComponent
                                    compact
                                    :text="$t('조회된 다운로드가 없습니다.')"
                                    :loading="listLoading"
                                    :loadingText="$t('다운로드 목록을 조회 중입니다.')"
                                    :error="listLoading ? '' : loadError"
                                    @retry="refreshList"
                                />
                            </template>

                            <template #head(selected)>
                                <b-form-checkbox 
                                    v-model="selectAll"
                                    :indeterminate="isIndeterminate"
                                    :disabled="downloadProgress.active"
                                    @change="toggleSelectAll"
                                />
                            </template>

                            <template #cell(selected)="row">
                                <b-form-checkbox 
                                    v-if="isSelectable(row.item)"
                                    v-model="row.item.selected"
                                    :disabled="downloadProgress.active"
                                    @change="toggleFileSelection(row.item)"
                                />
                            </template>

                            <template #cell(filename)="row">
                                <span v-b-tooltip.hover="row.value" class="text-truncate d-block">
                                    {{ row.value }}
                                </span>
                            </template>

                            <template #cell(fileSize)="row">
                                <span class="text-muted">{{ formatFileSize(row.value) }}</span>
                            </template>

                            <template #cell(status)="row">
                                <b-badge :variant="getStatusVariant(row.value)">
                                    {{ getStatusText(row.value) }}
                                </b-badge>
                            </template>

                            <template #cell(requestUserId)="row">
                                {{ row.value || 'N/A' }}
                            </template>

                            <template #cell(createdAt)="row">
                                {{ getDate(row.value) }}
                            </template>

                            <template #cell(action)="row">
                                <b-button v-if="isSelectable(row.item)"
                                          size="sm"
                                          variant="outline-primary"
                                          :disabled="downloadProgress.active"
                                          @click="downloadFile(row.item)"
                                >
                                    다운로드
                                </b-button>
                                <b-button v-else
                                          size="sm"
                                          variant="outline-secondary"
                                          disabled
                                >
                                    준비중
                                </b-button>
                            </template>
                        </b-table>

                        <div class="pagination-container">
                            <b-pagination
                                ref="pagination"
                                v-model="pagination.currentPage"
                                :total-rows="pagination.totalRows"
                                :per-page="pagination.perPage"
                                first-number
                                last-number
                                :disabled="listLoading"
                                align="fill"
                                size="sm"
                                @change="(page)=>changeQuery({page})"
                            ></b-pagination>
                            <b-form-select
                                v-model="pagination.perPage"
                                class="ml-3 mb-3"
                                style="width: 100px"
                                size="sm"
                                :disabled="listLoading"
                                @change="(pageSize)=>changeQuery({pageSize})"
                            >
                                <b-form-select-option v-for="data in perPageOptions"
                                                      :key="`${data.text}-${data.value}`"
                                                      :value="data.value">{{ data.text }}
                                </b-form-select-option>
                            </b-form-select>
                        </div>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <!-- Selected Files Modal -->
        <DownloadCenterModal 
            ref="downloadCenterModal"
            :selected-files="selectedFiles"
            :downloading="downloadProgress.active"
            @remove-file="removeFromSelection"
            @clear-all="clearSelection"
            @download="handleDownload"
        />
    </b-container>
</template>

<script>
import dayjs from 'dayjs';
import { zipSync } from 'fflate';
import { getErrorMessage } from '@core/utils/api/errorMessage';
import {
    getDefaultPagination,
    getPerPageOptions
} from '@shared/components/admin/pages/Management/common-data';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import PaginationPageMixin from '@shared/mixin/pagination-page-mixins';
import {
    columns,
    statusOptions,
    searchFormValue,
    SIZE_WARNING_THRESHOLD
} from './data';
import DownloadCenterModal from './DownloadCenterModal.vue';

export default {
    name: 'ManageDownloadCenter',
    components: {
        DownloadCenterModal,
        PageEmptyComponent,
    },
    mixins: [PaginationPageMixin({searchForm: searchFormValue, pagination: getDefaultPagination(1, 20)})],

    data() {
        return {
            tableColumns: columns,
            selectedFilesMap: {},
            selectAll: false,
            autoRefreshInterval: null,
            // 마지막으로 조회한 조건(URL 에 적용된 검색). 새로고침·자동 갱신은 아직 '검색하기'를 누르지 않은 폼 값 대신 이것을 쓴다
            appliedParams: null,
            listSeq: 0,
            silentLoading: false,
            loadError: '',
            zipAbort: null,
            downloadProgress: {
                active: false,
                current: 0,
                total: 0,
                text: ''
            },
            perPageOptions: [
                ...getPerPageOptions(),
                { text: 'All', value: 9999 }
            ]
        };
    },
    computed: {
        getStatusText(){
            return (text) => {
                const option = statusOptions.find(opt => opt.value === text);
                return option ? option.text : text;
            };  
        },
        statusOptions() {
            return statusOptions;
        },
        sizeWarningThreshold() {
            return SIZE_WARNING_THRESHOLD;
        },
        selectedFiles() {
            return Object.values(this.selectedFilesMap);
        },
        selectedTotalSize() {
            return this.selectedFiles.reduce((sum, file) => sum + (file.fileSize || 0), 0);
        },
        isIndeterminate() {
            if (!this.searchResults || this.searchResults.length === 0) return false;
            const selectableItems = this.searchResults.filter(item => this.isSelectable(item));
            const selectedCount = selectableItems.filter(item => item.selected).length;
            return selectedCount > 0 && selectedCount < selectableItems.length;
        }
    },
    watch: {
        '$route': {
            handler() {
                this.setSearchDataFormQuery();
                this.fetchData();
            }
        }
    },
    mounted() {
        this.setSearchDataFormQuery();
        this.fetchData();
        this.startAutoRefresh();
    },
    beforeDestroy() {
        this.stopAutoRefresh();
    },
    methods: {
        getDate(date) {
            if (!date) return '';
            if (!dayjs(date).isValid()) return '잘못된 날짜 형식입니다.';
            return dayjs(date).format('YYYY-MM-DD HH:mm');
        },
        async searchData() {
            this.pagination.currentPage = 1;
            const checkSameQuery = await this.changeQuery({});
            if (checkSameQuery) {
                await this.fetchData();
            }
        },
        // mixin(검색·초기화·페이지 이동 → $route watcher)이 부르는 조회. 폼은 이때 URL 과 같다
        fetchData() {
            const params = {
                page: this.pagination.currentPage,
                per_page: this.pagination.perPage,
            };

            if (this.searchFormValue.status) {
                params.status = this.searchFormValue.status;
            }
            if (this.searchFormValue.filename) {
                params.filename = this.searchFormValue.filename;
            }
            if (this.searchFormValue.fromDate) {
                params.from_date = this.searchFormValue.fromDate;
            }
            if (this.searchFormValue.toDate) {
                params.to_date = this.searchFormValue.toDate;
            }
            if (this.searchFormValue.includeOthers) {
                params.include_others = true;
            }
            this.appliedParams = params;
            return this.loadList(params);
        },
        // 새로고침·다시 시도: 입력 중인 폼 값이 아니라 마지막으로 조회한 조건으로
        refreshList() {
            return this.appliedParams ? this.loadList(this.appliedParams) : this.fetchData();
        },
        // silent: 자동 갱신 — 폼·버튼을 잠그지 않고 결과만 바꾼다. 실패해도 다음 주기에 다시 시도하므로 알리지 않는다
        async loadList(params, silent = false) {
            // 사용자 검색 뒤에 도착한 이전 응답(자동 갱신 포함)은 버린다
            const seq = ++this.listSeq;
            if (silent) this.silentLoading = true;
            else this.listLoading = true;
            try {
                const res = await this.$axios.get('/download_center', { params });
                if (seq !== this.listSeq) return;
                this.loadError = '';

                if (res.data?.data == null) {
                    this.searchResults = [];
                    this.pagination.totalRows = 0;
                    return;
                }

                const { downloads, totalCount } = res.data.data;

                this.searchResults = downloads.map((item) => {
                    const fullFilename = `${item.filename || ''}${item.ext || ''}`;
                    return {
                        ...item,
                        filename: fullFilename,
                        selected: !!this.selectedFilesMap[item.id]
                    };
                });

                this.pagination.totalRows = totalCount;
                this.updateSelectAllState();

            } catch (error) {
                if (seq !== this.listSeq || silent || error?.response?.status === 401) return;
                console.error('Fetch downloads error:', error);
                // 토스트를 놓치면 '없습니다'로 오해하므로 목록 자리에 남긴다
                this.loadError = getErrorMessage(error, '다운로드 목록을 불러오지 못했습니다.');
            } finally {
                if (silent) this.silentLoading = false;
                else if (seq === this.listSeq) this.listLoading = false;
            }
        },
        isSelectable(item) {
            return item.status === 'completed' && item.s3Key;
        },
        getStatusVariant(status) {
            const variants = {
                pending: 'warning',
                processing: 'info',
                completed: 'success',
                failed: 'danger'
            };
            return variants[status] || 'secondary';
        },
        formatFileSize(bytes) {
            if(bytes === '') return 'Unknown';
            if (bytes === null || bytes === undefined) return 'Unknown';
            if (bytes === 0) return '0 B';
            
            const k = 1024;
            const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        // Selection Methods
        toggleFileSelection(item) {
            if (item.selected) {
                this.$set(this.selectedFilesMap, item.id, {
                    id: item.id,
                    filename: item.filename,
                    fileSize: item.fileSize,
                    requestUserId: item.requestUserId,
                    s3Key: item.s3Key
                });
            } else {
                this.$delete(this.selectedFilesMap, item.id);
            }
            this.updateSelectAllState();
        },
        toggleSelectAll(checked) {
            if (!this.searchResults) return;
            
            this.searchResults.forEach(item => {
                if (this.isSelectable(item)) {
                    item.selected = checked;
                    if (checked) {
                        this.$set(this.selectedFilesMap, item.id, {
                            id: item.id,
                            filename: item.filename,
                            fileSize: item.fileSize,
                            s3Key: item.s3Key
                        });
                    } else {
                        this.$delete(this.selectedFilesMap, item.id);
                    }
                }
            });
        },
        updateSelectAllState() {
            if (!this.searchResults || this.searchResults.length === 0) {
                this.selectAll = false;
                return;
            }
            const selectableItems = this.searchResults.filter(item => this.isSelectable(item));
            if (selectableItems.length === 0) {
                this.selectAll = false;
                return;
            }
            const allSelected = selectableItems.every(item => item.selected);
            this.selectAll = allSelected;
        },
        clearSelection() {
            this.selectedFilesMap = {};
            if (this.searchResults) {
                this.searchResults.forEach(item => {
                    item.selected = false;
                });
            }
            this.selectAll = false;
        },
        removeFromSelection(id) {
            this.$delete(this.selectedFilesMap, id);
            if (this.searchResults) {
                const item = this.searchResults.find(i => i.id === id);
                if (item) {
                    item.selected = false;
                }
            }
        },
        showSelectedFilesModal() {
            this.$bvModal.show('selected-files-modal');
        },

        // Download Methods
        async downloadFile(item) {
            try {
                this.$EventBus.$emit('toast-info', '다운로드 준비', '다운로드 링크를 생성 중입니다...');

                const res = await this.$axios.get(`/download_center/${item.id}/presigned-url`);

                if (res.data?.data?.presignedUrl) {
                    this.startFileDownload(res.data.data.presignedUrl);
                    this.$EventBus.$emit('toast-success', '다운로드 요청', '다운로드를 요청했습니다. 브라우저 다운로드 목록에서 확인하세요.');
                } else {
                    this.$EventBus.$emit('toast-failure', '다운로드 실패', '다운로드 링크 생성에 실패했습니다.');
                }
            } catch (error) {
                if (error?.response?.status === 401) return;
                console.error('Download error:', error);
                this.$EventBus.$emit('toast-failure', '다운로드 오류', '다운로드 중 오류가 발생했습니다.');
            }
        },
        async downloadSelectedFiles(asZip) {
            if (this.selectedFiles.length === 0) {
                this.$EventBus.$emit('toast-warning', '선택 없음', '다운로드할 파일을 선택해주세요.');
                return;
            }

            const files = this.selectedFiles;

            if (asZip) {
                await this.downloadAsZip(files);
            } else {
                await this.downloadSeparately(files);
            }
        },
        handleDownload({ files, asZip }) {
            if (asZip) {
                this.downloadAsZip(files);
            } else {
                this.downloadSeparately(files);
            }
        },
        // 새 창(target=_blank) 대신 숨은 iframe 으로 받는다. 링크 발급(await) 뒤에 여는 새 창은 두 번째 파일부터 팝업 차단될 수 있고
        // 차단을 알 길이 없다. presigned URL 은 Content-Disposition: attachment 라(BE download_center.get_presigned_url) 화면 이동 없이 저장된다
        startFileDownload(url) {
            const frame = document.createElement('iframe');
            frame.style.display = 'none';
            frame.src = url;
            document.body.appendChild(frame);
            setTimeout(() => frame.remove(), 60000);
        },
        // 일부 파일을 받지 못했을 때 어떤 파일이 빠졌는지 알린다 (성공 토스트로 덮지 않는다)
        reportDownloadFailures(total, failed) {
            const h = this.$createElement;
            this.$showModal({
                type: 'alert',
                state: 'warning',
                title: '일부 파일 다운로드 실패',
                message: h('div', {style: {whiteSpace: 'pre-line'}},
                    `${total}개 중 ${failed.length}개를 받지 못했습니다. 해당 파일을 다시 선택해 받아주세요.\n${failed.join('\n')}`),
            });
        },
        async downloadSeparately(files) {
            const failed = [];
            this.downloadProgress = {
                active: true,
                current: 0,
                total: files.length,
                text: '다운로드 시작...'
            };

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                this.downloadProgress.current = i;
                this.downloadProgress.text = `${file.filename} 다운로드 중...`;

                try {
                    const res = await this.$axios.get(`/download_center/${file.id}/presigned-url`);

                    if (res.data?.data?.presignedUrl) {
                        this.startFileDownload(res.data.data.presignedUrl);
                        await new Promise(resolve => setTimeout(resolve, 500));
                    } else {
                        failed.push(file.filename);
                    }
                } catch (error) {
                    console.error(`Failed to download ${file.filename}:`, error);
                    failed.push(file.filename);
                }
            }

            this.downloadProgress.current = files.length;
            this.downloadProgress.text = '다운로드 요청 완료';
            setTimeout(() => {
                this.downloadProgress.active = false;
            }, 2000);
            if (failed.length) {
                this.reportDownloadFailures(files.length, failed);
            } else {
                this.$EventBus.$emit('toast-success', '다운로드 요청', `${files.length}개 파일 다운로드를 요청했습니다. 브라우저 다운로드 목록에서 확인하세요.`);
            }
        },
        async downloadAsZip(files) {
            // 모든 파일을 브라우저 메모리에 올려 압축한다 — 크면 먼저 확인받는다
            const totalSize = files.reduce((sum, file) => sum + (file.fileSize || 0), 0);
            if (totalSize > SIZE_WARNING_THRESHOLD) {
                const ok = await this.$showModal({
                    type: 'confirm',
                    state: 'warning',
                    title: '대용량 ZIP 다운로드',
                    message: `선택한 ${files.length}개 파일(약 ${this.formatFileSize(totalSize)})을 브라우저 메모리에서 압축합니다. `
                        + '압축하는 동안 화면이 멈출 수 있어 개별 다운로드를 권장합니다. 계속하시겠습니까?',
                });
                if (!ok) return;
            }
            const failed = [];
            // 취소는 파일을 받는 단계에서만 된다 (압축 zipSync 는 중간에 멈출 수 없지만 시간 대부분은 받는 단계)
            const abort = new AbortController();
            this.zipAbort = abort;
            this.downloadProgress = {
                active: true,
                current: 0,
                total: files.length,
                text: 'ZIP 파일 준비 중...'
            };

            try {
                const zipFiles = {};
                const usedFilenames = new Set();
                let completedCount = 0;

                for (const file of files) {
                    if (abort.signal.aborted) break;
                    this.downloadProgress.current = completedCount;
                    this.downloadProgress.text = `${file.filename} 가져오는 중...`;

                    try {
                        const urlRes = await this.$axios.get(`/download_center/${file.id}/presigned-url`);

                        if (abort.signal.aborted) break;
                        if (!urlRes.data?.data?.presignedUrl) {
                            console.error(`Failed to get URL for ${file.filename}`);
                            failed.push(file.filename);
                            continue;
                        }

                        const fileResponse = await fetch(urlRes.data.data.presignedUrl, { signal: abort.signal });
                        if (!fileResponse.ok) {
                            console.error(`Failed to fetch ${file.filename}`);
                            failed.push(file.filename);
                            continue;
                        }

                        const arrayBuffer = await fileResponse.arrayBuffer();

                        let zipFilename = file.filename;
                        if (usedFilenames.has(file.filename)) {
                            const lastDotIndex = file.filename.lastIndexOf('.');
                            if (lastDotIndex > 0) {
                                const name = file.filename.substring(0, lastDotIndex);
                                const ext = file.filename.substring(lastDotIndex);
                                zipFilename = `${name}_${file.id}${ext}`;
                            } else {
                                zipFilename = `${file.filename}_${file.id}`;
                            }
                        }
                        usedFilenames.add(file.filename);

                        zipFiles[zipFilename] = new Uint8Array(arrayBuffer);
                        completedCount++;
                    } catch (error) {
                        if (abort.signal.aborted) break;
                        console.error(`Error processing ${file.filename}:`, error);
                        failed.push(file.filename);
                    }
                }

                if (abort.signal.aborted) {
                    this.downloadProgress.active = false;
                    this.$EventBus.$emit('toast-warning', 'ZIP 다운로드 취소', 'ZIP 다운로드를 취소했습니다.');
                    return;
                }
                this.zipAbort = null;

                if (Object.keys(zipFiles).length === 0) {
                    this.$EventBus.$emit('toast-failure', '다운로드 실패', '다운로드할 수 있는 파일이 없습니다.');
                    this.downloadProgress.active = false;
                    return;
                }

                this.downloadProgress.current = files.length;
                this.downloadProgress.text = 'ZIP 파일 생성 중...';

                const zipped = zipSync(zipFiles, { level: 6 });
                const blob = new Blob([zipped], { type: 'application/zip' });

                const today = new Date().toISOString().split('T')[0];
                const zipFilename = `downloads_${today}.zip`;

                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = zipFilename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);

                this.downloadProgress.text = 'ZIP 다운로드 완료!';
                setTimeout(() => {
                    this.downloadProgress.active = false;
                }, 2000);
                if (failed.length) {
                    this.reportDownloadFailures(files.length, failed);
                } else {
                    this.$EventBus.$emit('toast-success', 'ZIP 다운로드 완료', `${Object.keys(zipFiles).length}개 파일이 포함된 ZIP 파일이 생성되었습니다.`);
                }

            } catch (error) {
                console.error('ZIP creation error:', error);
                this.$EventBus.$emit('toast-failure', 'ZIP 생성 실패', 'ZIP 파일 생성 중 오류가 발생했습니다: ' + error.message);
                this.downloadProgress.active = false;
            } finally {
                this.zipAbort = null;
            }
        },
        cancelZip() {
            if (this.zipAbort) this.zipAbort.abort();
        },

        // Auto Refresh
        startAutoRefresh() {
            this.autoRefreshInterval = setInterval(this.autoRefresh, 30000); // 30 seconds
        },
        // 처리중 파일이 완료되는지 보려는 갱신 — 마지막으로 조회한 조건으로 조용히, 사용자가 보지 않거나 다른 작업 중이면 건너뛴다
        autoRefresh() {
            if (document.hidden || this.listLoading || this.silentLoading || this.downloadProgress.active || !this.appliedParams) return;
            this.loadList(this.appliedParams, true);
        },
        stopAutoRefresh() {
            if (this.autoRefreshInterval) {
                clearInterval(this.autoRefreshInterval);
                this.autoRefreshInterval = null;
            }
        }
    }
};
</script>

<style lang="scss" scoped>

.selection-info {
    font-size: 14px;
}

.table-container {
    overflow-x: auto;
}

.pagination-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 16px;
    border-top: 1px solid #e9ecef;
}

.text-truncate {
    max-width: 250px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>

